/**
 * PsbStage7Page — Route B Calculation, Reconciliation & Report
 *
 * 4-step wizard:
 *   Step 1 — Select an applied Stage 6C session
 *   Step 2 — Source coverage + exception register
 *   Step 3 — Product / headline reconciliation
 *   Step 4 — Acceptance gate (blocked while material exceptions remain)
 */

import { getBackendUrl } from '@/lib/backendUrl';
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, BarChart3, CheckCircle2, ChevronDown, ChevronRight, ChevronUp,
  ClipboardList, Database, Info, Loader2, RefreshCw, ShieldAlert, XCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API = getBackendUrl();

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt(v, dp = 2) {
  if (v == null) return '—';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}
function fmtPct(v) {
  if (v == null) return '—';
  return `${Number(v).toFixed(2)}%`;
}
function shortId(v) { return v ? String(v).slice(0, 8) : '—'; }

function Stat({ label, value, mono, highlight, testid }) {
  const hi =
    highlight === 'red' ? 'text-red-700' :
    highlight === 'amber' ? 'text-amber-700' :
    highlight === 'green' ? 'text-emerald-700' :
    'text-slate-800';
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-sm font-semibold ${mono ? 'font-mono' : ''} ${hi}`}>{value}</p>
    </div>
  );
}

// ── Step indicators ───────────────────────────────────────────────────────────

function StepHeader({ step }) {
  const steps = [
    { n: 1, label: 'Session' },
    { n: 2, label: 'Coverage' },
    { n: 3, label: 'Reconciliation' },
    { n: 4, label: 'Accept' },
  ];
  return (
    <div className="flex items-center gap-0 mb-6">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all
            ${step === s.n ? 'bg-indigo-600 text-white' :
              step > s.n ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
            {step > s.n ? <CheckCircle2 size={12} /> : <span>{s.n}</span>}
            <span>{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 ${step > s.n ? 'bg-emerald-300' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Coverage badge ─────────────────────────────────────────────────────────────

function CoverageBadge({ pct, blocked }) {
  const pctNum = Number(pct || 0);
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border
      ${blocked ? 'text-red-700 bg-red-50 border-red-200' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`}
      data-testid="s7-coverage-badge">
      {blocked ? <XCircle size={12} /> : <CheckCircle2 size={12} />}
      {pctNum.toFixed(2)}% covered
    </span>
  );
}

// ── Fingerprint chain ─────────────────────────────────────────────────────────

function FingerprintChain({ plan }) {
  if (!plan) return null;
  const labels = ['S0', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6A', 'S6B', 'S6C', 'S7'];
  const ids = [
    plan.stage0_run_id, plan.s1_run_id, plan.s2_plan_id, plan.s3_plan_id,
    plan.s4_plan_id, plan.s5_plan_id, plan.s6a_plan_id, plan.s6b_plan_id,
    plan.s6c_plan_id, plan.run_id,
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="s7-fingerprint-chain">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Source Fingerprint Chain</p>
      <div className="flex items-center flex-wrap gap-1">
        {labels.map((lbl, i) => {
          const id = ids[i];
          const shortid = id ? String(id).slice(0, 8) : null;
          return (
            <React.Fragment key={lbl}>
              <div className={`flex flex-col items-center ${!shortid ? 'opacity-40' : ''}`}>
                <span className="text-[9px] font-semibold text-slate-500 uppercase">{lbl}</span>
                <code className="text-[10px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">
                  {shortid || '—'}
                </code>
              </div>
              {i < labels.length - 1 && <span className="text-slate-300 text-xs font-bold px-0.5">→</span>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Exception register table ──────────────────────────────────────────────────

function ExceptionRegister({ exceptions, summary }) {
  const [showAll, setShowAll] = useState(false);
  if (!exceptions || exceptions.length === 0) return null;
  const visible = showAll ? exceptions : exceptions.slice(0, 10);
  return (
    <div className="rounded-xl border border-red-200 bg-red-50/40 p-4" data-testid="s7-exception-register">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">
          Exception Register — {exceptions.length} unmapped products
        </p>
        <div className="flex gap-2 text-xs text-red-600">
          <span>Held PLU (inc-VAT): <strong>{fmt(summary?.held_plu_value_inc_vat)}</strong></span>
          <span>Held Open: <strong>{fmt(summary?.held_open_cost)}</strong></span>
          <span>Held Close: <strong>{fmt(summary?.held_close_cost)}</strong></span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" data-testid="s7-exception-table">
          <thead className="bg-red-100/60 border-b border-red-200">
            <tr>
              <th className="text-left px-2 py-1.5 font-semibold text-red-700">Item Code</th>
              <th className="text-left px-2 py-1.5 font-semibold text-red-700">Name</th>
              <th className="text-right px-2 py-1.5 font-semibold text-red-700">Hold Reason</th>
              <th className="text-right px-2 py-1.5 font-semibold text-red-700">PLU Legacy £</th>
              <th className="text-right px-2 py-1.5 font-semibold text-red-700">Open Cost £</th>
              <th className="text-right px-2 py-1.5 font-semibold text-red-700">Close Cost £</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e, i) => (
              <tr key={e.id || i} className={`border-b border-red-100 ${i % 2 === 0 ? 'bg-white/60' : 'bg-red-50/30'}`}>
                <td className="px-2 py-1 font-mono text-red-800 font-semibold">{e.itemcode || '—'}</td>
                <td className="px-2 py-1 text-slate-600 max-w-[180px] truncate">{e.item_name || '—'}</td>
                <td className="px-2 py-1 text-right text-slate-500 text-[10px]">{e.hold_reason || '—'}</td>
                <td className="px-2 py-1 text-right font-mono text-red-700">{fmt(e.plu_legacy_value, 2)}</td>
                <td className="px-2 py-1 text-right font-mono text-slate-600">{fmt(e.open_cost, 4)}</td>
                <td className="px-2 py-1 text-right font-mono text-slate-600">{fmt(e.close_cost, 4)}</td>
              </tr>
            ))}
          </tbody>
          {!showAll && exceptions.length > 10 && (
            <tfoot>
              <tr>
                <td colSpan={6} className="px-2 py-2 text-center">
                  <button className="text-xs text-red-600 underline hover:text-red-800"
                    onClick={() => setShowAll(true)}>
                    Show all {exceptions.length} entries
                  </button>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Residual bridge ───────────────────────────────────────────────────────────

function ResidualBridge({ headline, plan }) {
  if (!headline) return null;
  const vatRate = headline.plu_vat_rate_applied != null
    ? `${(headline.plu_vat_rate_applied * 100).toFixed(2)}%`
    : '20.00%';
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="s7-residual-bridge">
      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-3">
        Residual Bridge — Separate Accounting Domains
      </p>

      {/* Domain 1: PLU Revenue */}
      <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1 mt-2">
        PLU Domain — Revenue (inc-VAT, TRANFILE bar prices)
      </p>
      <table className="w-full text-xs mb-4">
        <tbody className="divide-y divide-amber-100">
          <tr>
            <td className="py-1 text-slate-600">
              Total PLU legacy (inc-VAT, all {headline.total_products} products)
            </td>
            <td className="py-1 text-right font-mono font-semibold text-slate-800">
              {fmt(headline.plu_total_inc_vat)}
            </td>
          </tr>
          <tr>
            <td className="py-1 text-emerald-700 pl-3">
              Covered PLU inc-VAT ({headline.covered_products} product{headline.covered_products !== 1 ? 's' : ''})
            </td>
            <td className="py-1 text-right font-mono text-emerald-700">
              − {fmt(headline.covered_plu_retail)}
            </td>
          </tr>
          <tr className="font-semibold border-t border-amber-300">
            <td className="py-1 text-red-700">
              Held PLU residual (inc-VAT, {headline.held_products} products)
            </td>
            <td className="py-1 text-right font-mono text-red-700">
              {fmt(headline.held_plu_legacy)}
            </td>
          </tr>
          <tr className="border-t border-amber-200">
            <td className="py-1 text-slate-500">
              Accounts takings (inc-VAT, Stage 6B)
            </td>
            <td className="py-1 text-right font-mono text-slate-700">
              {fmt(headline.takings_inc_vat)}
            </td>
          </tr>
          <tr className="font-semibold">
            <td className="py-1 text-slate-700">PLU vs Takings (both inc-VAT)</td>
            <td className={`py-1 text-right font-mono ${
              Math.abs(headline.plu_vs_takings_inc_vat || 0) < 0.01
                ? 'text-emerald-700' : 'text-amber-700'}`}
              data-testid="s7-plu-vs-takings-inc">
              {headline.plu_vs_takings_inc_vat != null
                ? `${headline.plu_vs_takings_inc_vat >= 0 ? '+' : ''}${fmt(headline.plu_vs_takings_inc_vat)}`
                : '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">
        PLU Domain — Revenue (ex-VAT derived, session-implied {vatRate})
      </p>
      <table className="w-full text-xs mb-4">
        <tbody className="divide-y divide-amber-100">
          <tr>
            <td className="py-1 text-slate-600">
              Total PLU (ex-VAT ÷{vatRate}, all {headline.total_products} products)
            </td>
            <td className="py-1 text-right font-mono text-slate-800">
              {fmt(headline.plu_total_exc_vat)}
            </td>
          </tr>
          <tr>
            <td className="py-1 text-slate-500">
              Accounts takings (ex-VAT, Stage 6B)
            </td>
            <td className="py-1 text-right font-mono text-slate-700">
              {fmt(headline.takings_exc_vat)}
            </td>
          </tr>
          <tr className="font-semibold">
            <td className="py-1 text-slate-700">PLU vs Takings (both ex-VAT)</td>
            <td className={`py-1 text-right font-mono ${
              Math.abs(headline.plu_vs_takings_exc_vat || 0) < 0.01
                ? 'text-emerald-700' : 'text-amber-700'}`}
              data-testid="s7-plu-vs-takings-exc">
              {headline.plu_vs_takings_exc_vat != null
                ? `${headline.plu_vs_takings_exc_vat >= 0 ? '+' : ''}${fmt(headline.plu_vs_takings_exc_vat)}`
                : '—'}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Domain 2: Stock Cost — NOT added to PLU */}
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
        Stock Cost Domain — Cost basis (separate, not comparable with PLU revenue)
      </p>
      <table className="w-full text-xs">
        <tbody className="divide-y divide-slate-100">
          <tr>
            <td className="py-1 text-slate-500 pl-3">Held opening cost ({headline.held_products} products)</td>
            <td className="py-1 text-right font-mono text-slate-600">{fmt(headline.held_open_cost, 4)}</td>
          </tr>
          <tr>
            <td className="py-1 text-slate-500 pl-3">Held closing cost ({headline.held_products} products)</td>
            <td className="py-1 text-right font-mono text-slate-600">{fmt(headline.held_close_cost, 4)}</td>
          </tr>
        </tbody>
      </table>
      <p className="text-[10px] text-slate-400 mt-1 italic">
        * Stock cost figures above are a different accounting domain from PLU revenue and must not be summed with it.
      </p>
    </div>
  );
}

// ── Headline card ─────────────────────────────────────────────────────────────

function HeadlineCard({ headline, ytd, blocked }) {
  if (!headline) return null;
  return (
    <div className="space-y-4">
      {/* Covered sub-totals */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="s7-headline-covered">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Route B — Covered Products Only ({headline.covered_products} / {headline.total_products})
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Opening (cost)" value={fmt(headline.covered_open_cost, 4)} mono testid="hl-open" />
          <Stat label="Deliveries (cost)" value={fmt(headline.covered_del_cost, 4)} mono testid="hl-del" />
          <Stat label="Credits (cost)" value={fmt(headline.covered_cred_cost, 4)} mono testid="hl-cred" />
          <Stat label="Closing (cost)" value={fmt(headline.covered_close_cost, 4)} mono testid="hl-close" />
          <Stat label="Consumed (cost)" value={fmt(headline.covered_consumed_cost, 4)} mono testid="hl-consumed" />
          <Stat label="PLU retail (covered)" value={fmt(headline.covered_plu_retail, 4)} mono testid="hl-plu-retail" />
          <Stat label="PLU cost (covered)" value={fmt(headline.covered_plu_cost, 4)} mono testid="hl-plu-cost" />
          <Stat label="Result (cost)"
            value={fmt(headline.covered_result_cost, 4)}
            mono highlight={headline.covered_result_cost > 0 ? 'amber' : 'green'}
            testid="hl-result" />
        </div>
        {headline.covered_gp_pct != null && (
          <p className="mt-3 text-xs text-slate-500">
            GP% (covered products only): <strong>{fmtPct(headline.covered_gp_pct)}</strong>
          </p>
        )}
      </div>

      {/* Takings & adjustments */}
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-4" data-testid="s7-headline-takings">
        <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-3">Takings & Adjustments</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Takings (inc VAT)" value={fmt(headline.takings_inc_vat)} mono testid="hl-tak-inc" />
          <Stat label="Takings (exc VAT)" value={fmt(headline.takings_exc_vat)} mono testid="hl-tak-exc" />
          <Stat label="Allowances (incl.)" value={fmt(headline.allowances_included, 4)} mono testid="hl-allowances" />
          <Stat label="Management (excl.)" value={fmt(headline.management_excluded, 4)} mono testid="hl-mgmt" />
        </div>
      </div>

      {/* YTD */}
      {ytd && ytd.actual_receipts_inc_vat != null && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="s7-ytd-panel">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Previous YTD (Stage 5)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Prev actual receipts (inc VAT)" value={fmt(ytd.actual_receipts_inc_vat)} mono testid="ytd-receipts" />
            <Stat label="Prev result" value={fmt(ytd.result_inc_vat, 4)} mono testid="ytd-result" />
            <Stat label="Prev gross profit" value={fmt(ytd.gross_profit)} mono testid="ytd-gp" />
            <Stat label="Source type" value={ytd.source_type || '—'} testid="ytd-source" />
          </div>
        </div>
      )}

      {blocked && (
        <div className="rounded-lg bg-red-50 border border-red-300 px-3 py-2 flex items-start gap-2" data-testid="s7-headline-blocked-note">
          <AlertTriangle size={13} className="text-red-600 mt-0.5 shrink-0" />
          <p className="text-xs text-red-700">
            <strong>Headline totals above are PARTIAL (covered products only).</strong>{' '}
            {headline.held_products} products ({fmt(headline.held_plu_legacy)} PLU) are in the exception register
            and are NOT included in these totals. Do not treat these sub-totals as the complete Route B result.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Product lines table ───────────────────────────────────────────────────────

function ProductLinesTable({ lines }) {
  if (!lines || lines.length === 0) {
    return <p className="text-xs text-slate-400">No covered product lines.</p>;
  }
  return (
    <div className="overflow-x-auto" data-testid="s7-product-lines-table">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
          <tr>
            <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Item</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Open £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Del £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Cred £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Close £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Consumed £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">PLU Retail £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">PLU Cost £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Result £</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">GP%</th>
            <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Yield%</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.id || l.itemcode} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <td className="px-2 py-1.5 font-mono font-semibold text-slate-800">{l.itemcode}<br />
                <span className="font-sans font-normal text-slate-400 text-[10px]">{l.bp_name}</span>
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-600">{fmt(l.open_value, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-600">{fmt(l.del_value, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-600">{fmt(l.cred_value, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-600">{fmt(l.close_value, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-700 font-semibold">{fmt(l.consumed_value, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-indigo-700">{fmt(l.plu_retail_value, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-600">{fmt(l.plu_cost_value, 4)}</td>
              <td className={`px-2 py-1.5 text-right font-mono font-semibold ${l.result_value > 0.01 ? 'text-amber-700' : l.result_value < -0.01 ? 'text-emerald-700' : 'text-slate-600'}`}>
                {fmt(l.result_value, 4)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-600">{l.gp_pct != null ? fmtPct(l.gp_pct) : '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-600">{l.yield_pct != null ? fmtPct(l.yield_pct) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Bulk Exception Resolver ───────────────────────────────────────────────────

const CATEGORY_COLORS = {
  exact:            { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  strong_suggestion:{ bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   badge: 'bg-amber-100 text-amber-700' },
  review_required:  { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700',  badge: 'bg-orange-100 text-orange-700' },
  blocked:          { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     badge: 'bg-red-100 text-red-700' },
  proposed_new:     { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-600',   badge: 'bg-slate-100 text-slate-600' },
};

function BulkExceptionResolver({ planId, headers, onResolved }) {
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [resolving, setResolving]   = useState(false);
  const [expanded, setExpanded]     = useState({});
  const [resolveResult, setResolveResult] = useState(null);

  const loadCandidates = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/psb-stage7/plans/${planId}/exception-candidates`, { headers });
      setCandidates(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load exception candidates');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const handleAutoResolve = useCallback(async () => {
    setResolving(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/psb-stage7/plans/${planId}/auto-resolve-exact`,
        {}, { headers });
      setResolveResult(r.data);
      toast.success(`Auto-resolved ${r.data.resolved_count} exact-match products.`);
      if (onResolved) onResolved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-resolve failed');
    } finally {
      setResolving(false);
    }
  }, [planId, onResolved]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 py-2" data-testid="s7-resolver-loading">
        <Loader2 size={13} className="animate-spin" />Loading exception candidates…
      </div>
    );
  }

  if (!candidates) return null;
  const cats = candidates.categories || {};

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4"
      data-testid="s7-bulk-resolver">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Bulk Exception Resolver
        </p>
        {resolveResult ? (
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
            data-testid="s7-resolve-result-badge">
            {resolveResult.resolved_count} resolved
          </span>
        ) : candidates.auto_resolvable_count > 0 ? (
          <button
            onClick={handleAutoResolve}
            disabled={resolving}
            className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 disabled:opacity-50"
            data-testid="s7-auto-resolve-btn">
            {resolving
              ? <><Loader2 size={12} className="animate-spin" />Resolving…</>
              : <>Auto-resolve {candidates.auto_resolvable_count} exact match{candidates.auto_resolvable_count !== 1 ? 'es' : ''} (£{candidates.auto_resolvable_plu_inc_vat?.toFixed(2)} PLU inc-VAT)</>}
          </button>
        ) : (
          <span className="text-xs text-slate-400">No auto-resolvable candidates</span>
        )}
      </div>

      {resolveResult?.opening_stock_warning && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2"
          data-testid="s7-opening-stock-warning">
          <AlertTriangle size={13} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700">{resolveResult.opening_stock_warning}</p>
        </div>
      )}

      {/* Category summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(cats).map(([key, cat]) => {
          const c = CATEGORY_COLORS[key] || CATEGORY_COLORS.blocked;
          return (
            <div key={key}
              className={`rounded-lg border ${c.border} ${c.bg} p-2 cursor-pointer hover:shadow-sm transition-shadow`}
              onClick={() => setExpanded(e => ({ ...e, [key]: !e[key] }))}
              data-testid={`s7-cat-${key}`}>
              <span className={`text-[10px] font-bold ${c.text} uppercase tracking-wide`}>{key.replace('_', ' ')}</span>
              <p className={`text-lg font-bold ${c.text}`}>{cat.count}</p>
              <p className="text-[10px] text-slate-500 font-mono">£{(cat.total_plu_inc_vat || 0).toFixed(2)}</p>
            </div>
          );
        })}
      </div>

      {/* Expanded rows per category */}
      {Object.entries(cats).map(([key, cat]) => {
        if (!expanded[key] || !cat.rows?.length) return null;
        const c = CATEGORY_COLORS[key] || CATEGORY_COLORS.blocked;
        return (
          <div key={key} className={`rounded-lg border ${c.border} ${c.bg} overflow-x-auto`}
            data-testid={`s7-cat-rows-${key}`}>
            <table className="w-full text-[11px] border-collapse">
              <thead className="bg-white/60 border-b border-slate-200">
                <tr>
                  <th className="text-left px-2 py-1 font-semibold text-slate-600">Code</th>
                  <th className="text-left px-2 py-1 font-semibold text-slate-600">Source Description</th>
                  <th className="text-left px-2 py-1 font-semibold text-slate-600">Matched BP</th>
                  <th className="text-right px-2 py-1 font-semibold text-slate-600">PLU inc-VAT £</th>
                </tr>
              </thead>
              <tbody>
                {cat.rows.map((row, i) => (
                  <tr key={row.mc_id || i} className="border-b border-white/60">
                    <td className="px-2 py-1 font-mono font-semibold text-slate-800">{row.itemcode}</td>
                    <td className="px-2 py-1 text-slate-600 max-w-[200px] truncate">{row.source_description}</td>
                    <td className="px-2 py-1 text-slate-500 max-w-[180px] truncate">{row.matched_bp_name || '—'}</td>
                    <td className="px-2 py-1 text-right font-mono text-slate-700">{fmt(row.plu_value_inc_vat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ── Source Bridge Panel ───────────────────────────────────────────────────────
//
// Lists the per-item bridge lines (Group 19 exception-register lines and S6C
// Reception adjustment lines) that require explicit accounting disposition
// before Stage 7 acceptance.  Read-only fetch for review; declare triggers a
// POST for each item.  No auto-declaration or auto-exclusion.

const BRIDGE_CLASSIFICATIONS = [
  { value: "", label: "— Select confirmed classification —" },
  { value: "management_allowance", label: "Management allowance adjustment" },
  { value: "stock_cost_adjustment", label: "Stock cost adjustment" },
  { value: "plu_retail_line",       label: "PLU retail line" },
  { value: "hybrid",                label: "Hybrid management/cost" },
  { value: "other",                 label: "Other — see treatment text" },
];

const BRIDGE_CLASSIFICATION_LABELS = Object.fromEntries(
  BRIDGE_CLASSIFICATIONS.slice(1).map(c => [c.value, c.label])
);

function SourceBridgePanel({ planId, headers, onDeclared }) {
  const [items, setItems]           = useState(null);
  const [loading, setLoading]       = useState(false);
  const [collapsed, setCollapsed]   = useState(false);
  const [declareForm, setDeclareForm] = useState({});
  // declareForm: { [itemId]: { classification, treatment, submitting, error } }

  const loadBridge = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/psb-stage7/plans/${planId}/source-bridge`,
        { headers }
      );
      setItems(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load source bridge items');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { loadBridge(); }, [loadBridge]);

  const initForm = (itemId) => setDeclareForm(f => ({
    ...f,
    [itemId]: f[itemId] || { classification: '', treatment: '', submitting: false, error: null },
  }));

  const setFormField = (itemId, field, value) => setDeclareForm(f => ({
    ...f, [itemId]: { ...f[itemId], [field]: value },
  }));

  const handleDeclare = async (item) => {
    const form = declareForm[item.id] || {};
    const classification = form.classification || '';
    const treatment      = (form.treatment || '').trim();
    if (!classification) {
      setFormField(item.id, 'error', 'Select a confirmed classification before submitting.');
      return;
    }
    if (treatment.length < 10) {
      setFormField(item.id, 'error', 'Final-result treatment must be at least 10 characters.');
      return;
    }
    const classLabel = BRIDGE_CLASSIFICATION_LABELS[classification] || classification;
    const declared_treatment = `${classLabel}: ${treatment}`;
    setFormField(item.id, 'submitting', true);
    setFormField(item.id, 'error', null);
    try {
      await axios.post(
        `${API}/api/admin/psb-stage7/plans/${planId}/source-bridge/${item.id}/declare`,
        { declared_treatment },
        { headers }
      );
      toast.success(`Declared: ${item.description || item.source_identifier}`);
      await loadBridge();
      if (onDeclared) onDeclared();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Declare failed';
      setFormField(item.id, 'error', typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally {
      setFormField(item.id, 'submitting', false);
    }
  };

  const pendingCount = items ? items.items.filter(i => i.requires_disposition).length : null;

  return (
    <div className={`rounded-xl border-2 ${items?.all_disposed ? 'border-emerald-300 bg-emerald-50/30' : 'border-orange-300 bg-orange-50/20'} p-4`}
      data-testid="s7-source-bridge-panel">

      {/* Header */}
      <div className="flex items-center justify-between cursor-pointer mb-1"
        onClick={() => setCollapsed(c => !c)}>
        <div className="flex items-center gap-2">
          <ShieldAlert size={15} className={items?.all_disposed ? 'text-emerald-600' : 'text-orange-600'} />
          <span className={`text-sm font-bold ${items?.all_disposed ? 'text-emerald-800' : 'text-orange-800'}`}>
            Source Bridge Items — Accounting Disposition Required
          </span>
          {pendingCount !== null && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border
              ${pendingCount === 0
                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                : 'bg-orange-100 text-orange-700 border-orange-200'}`}
              data-testid="s7-bridge-pending-count">
              {pendingCount === 0 ? 'All declared' : `${pendingCount} pending`}
            </span>
          )}
        </div>
        {collapsed ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronUp size={14} className="text-slate-400" />}
      </div>

      {/* Disclaimer */}
      {!collapsed && (
        <>
          <p className="text-[11px] text-slate-500 mb-3 ml-5">
            These lines appear in the exception register (Group 19) or as Stage 6C Reception adjustments.
            Stock-cost and PLU-retail effects are shown separately and must not be netted.
            Whether the two Reception entries overlap is unconfirmed — the authorised reviewer must
            classify each line individually. Declaring here records a provisional treatment only;
            the calculation result is unchanged.
          </p>

          {/* S6C linkage diagnostic — shown whenever s6c_adjustment_items_created == 0 */}
          {items?.s6c_diagnostic && items.s6c_diagnostic.s6c_adjustment_items_created === 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2.5 mb-2 space-y-1.5"
                 data-testid="s6c-linkage-diagnostic">
              <p className="text-[11px] font-semibold text-amber-800">S6C linkage diagnostic — no ALLOFILE bridge rows found</p>
              <p className="text-[10px] text-amber-700">{items.s6c_diagnostic.linkage_note}</p>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <span className="text-[10px] text-slate-600">S6C run found: <strong>{items.s6c_diagnostic.s6c_run_found_in_db ? 'Yes' : 'No'}</strong></span>
                <span className="text-[10px] text-slate-600">Total adj_links: <strong>{items.s6c_diagnostic.total_adj_links_for_run}</strong></span>
                <span className="text-[10px] text-slate-600">'reception' matches: <strong>{items.s6c_diagnostic.reception_name_matches}</strong></span>
              </div>
              {items.s6c_diagnostic.all_adj_names?.length > 0 && (
                <details className="mt-1">
                  <summary className="text-[10px] font-semibold text-amber-800 cursor-pointer">
                    All ALLOFILE names in this S6C run ({items.s6c_diagnostic.all_adj_names.length})
                  </summary>
                  <div className="mt-1 max-h-32 overflow-y-auto space-y-0.5">
                    {items.s6c_diagnostic.all_adj_names.map((n, idx) => (
                      <div key={idx} className="flex justify-between text-[10px] font-mono text-slate-600 bg-white rounded px-2 py-0.5">
                        <span>{n.name || '(blank)'}</span>
                        <span className="text-slate-400">{n.adjustment_class} · {n.value != null ? `£${n.value.toFixed(2)}` : '—'}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-2" data-testid="s7-bridge-loading">
          <Loader2 size={12} className="animate-spin" />Loading bridge items…
        </div>
      )}

      {!loading && !collapsed && items && (
        <div className="space-y-3" data-testid="s7-bridge-item-list">
          {items.items.map(item => (
            <div key={item.id}
              className={`rounded-lg border p-3 bg-white
                ${item.requires_disposition ? 'border-orange-200' : 'border-emerald-200'}`}
              data-testid={`bridge-item-${item.id}`}>

              {/* Item identity row */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-slate-800">
                    {item.description || item.source_identifier}
                  </span>
                  <div className="flex flex-wrap items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-mono text-slate-500">{item.source_identifier}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border
                      ${item.source_instrument === 'exception_register'
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                      {item.source_instrument === 'exception_register'
                        ? `G${item.group_code} Exception`
                        : 'S6C Reception'}
                    </span>
                    {item.is_allow != null && (
                      <span className="text-[10px] text-slate-500">
                        {item.is_allow ? 'allow=Y' : 'allow=N'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  {item.requires_disposition ? (
                    <span className="text-[10px] font-semibold text-orange-700 bg-orange-100 border border-orange-200 rounded px-1.5 py-0.5">
                      Pending
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
                      Declared
                    </span>
                  )}
                </div>
              </div>

              {/* Separate financial effects — do NOT net these */}
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Stock Cost Effect</p>
                  <p className={`text-xs font-mono font-semibold mt-0.5
                    ${item.stock_cost_gbp == null ? 'text-slate-400' :
                      item.stock_cost_gbp < 0 ? 'text-red-700' : 'text-emerald-700'}`}
                    data-testid={`bridge-stock-cost-${item.id}`}>
                    {item.stock_cost_gbp != null
                      ? `£${Number(item.stock_cost_gbp).toLocaleString('en-GB', { minimumFractionDigits: 4 })}`
                      : '—'}
                  </p>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">PLU Retail (inc-VAT)</p>
                  <p className={`text-xs font-mono font-semibold mt-0.5
                    ${item.plu_retail_inc_vat_gbp == null ? 'text-slate-400' :
                      item.plu_retail_inc_vat_gbp < 0 ? 'text-red-700' : 'text-indigo-700'}`}
                    data-testid={`bridge-plu-retail-${item.id}`}>
                    {item.plu_retail_inc_vat_gbp != null
                      ? `£${Number(item.plu_retail_inc_vat_gbp).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`
                      : '—'}
                  </p>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">S6C Value</p>
                  <p className={`text-xs font-mono font-semibold mt-0.5
                    ${item.s6c_value_gbp == null ? 'text-slate-400' :
                      item.s6c_value_gbp < 0 ? 'text-red-700' : 'text-slate-700'}`}
                    data-testid={`bridge-s6c-value-${item.id}`}>
                    {item.s6c_value_gbp != null
                      ? `£${Number(item.s6c_value_gbp).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`
                      : '—'}
                  </p>
                  {item.s6c_value_gbp == null && item.source_instrument === 'exception_register' && (
                    <p className="text-[9px] text-slate-400 mt-0.5 leading-tight">
                      PSB stock entry — ALLOFILE value is a separate row type
                    </p>
                  )}
                </div>
              </div>

              {/* Declared treatment (if already set) */}
              {!item.requires_disposition && item.declared_treatment && (
                <div className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 mb-2"
                  data-testid={`bridge-declared-${item.id}`}>
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-0.5">
                    Declared treatment
                  </p>
                  <p className="text-xs text-slate-700">{item.declared_treatment}</p>
                  {item.declared_at && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {new Date(item.declared_at).toLocaleString('en-GB')}
                    </p>
                  )}
                </div>
              )}

              {/* Declare form — visible when pending OR when user wants to revise */}
              {item.requires_disposition && (
                <div className="rounded border border-amber-200 bg-amber-50/50 px-3 py-2.5 space-y-2.5"
                  data-testid={`bridge-declare-form-${item.id}`}
                  onClick={() => initForm(item.id)}>

                  <p className="text-[11px] font-semibold text-amber-800">
                    Declare accounting treatment for this line
                  </p>

                  {/* Classification dropdown */}
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                      Confirmed classification <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={declareForm[item.id]?.classification || ''}
                      onChange={e => { initForm(item.id); setFormField(item.id, 'classification', e.target.value); }}
                      className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs bg-white focus:ring-1 focus:ring-amber-400"
                      data-testid={`bridge-classification-select-${item.id}`}
                    >
                      {BRIDGE_CLASSIFICATIONS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Final-result treatment textarea */}
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                      Final-result treatment <span className="text-red-500">*</span>
                      <span className="font-normal text-slate-400 ml-1">
                        — describe how this line affects the Stage 7 result and why
                      </span>
                    </label>
                    <textarea
                      value={declareForm[item.id]?.treatment || ''}
                      onChange={e => { initForm(item.id); setFormField(item.id, 'treatment', e.target.value); }}
                      rows={2}
                      placeholder="e.g. Confirmed as a management allowance — the £ value is already included in Stage 6B takings allowances and does not require a separate stock-cost adjustment."
                      className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs font-mono bg-white focus:ring-1 focus:ring-amber-400"
                      data-testid={`bridge-treatment-textarea-${item.id}`}
                    />
                  </div>

                  {/* Error */}
                  {declareForm[item.id]?.error && (
                    <p className="text-xs text-red-700" data-testid={`bridge-declare-error-${item.id}`}>
                      {declareForm[item.id].error}
                    </p>
                  )}

                  {/* Submit */}
                  <button
                    onClick={() => handleDeclare(item)}
                    disabled={declareForm[item.id]?.submitting}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                    data-testid={`bridge-declare-submit-${item.id}`}
                  >
                    {declareForm[item.id]?.submitting
                      ? <><RefreshCw size={11} className="animate-spin" />Saving…</>
                      : 'Record declaration'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Refresh */}
      {!collapsed && !loading && (
        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={loadBridge}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="s7-bridge-refresh-btn">
            <RefreshCw size={11} /> Refresh
          </button>
          {items?.all_disposed && (
            <span className="text-xs font-semibold text-emerald-700">
              All {items.items.length} bridge items declared — acceptance gate cleared
            </span>
          )}
        </div>
      )}
    </div>
  );
}


// ── Main page ─────────────────────────────────────────────────────────────────

export default function PsbStage7Page() {
  const [step, setStep] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [plan, setPlan] = useState(null);
  const [productLines, setProductLines] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptResult, setAcceptResult] = useState(null);

  const token = localStorage.getItem('access_token') || '';
  const headers = { Authorization: `Bearer ${token}` };
  // Load S6C sessions
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/admin/psb-stage7/s6c-sessions`, { headers })
      .then(r => setSessions(r.data.sessions || []))
      .catch(() => toast.error('Failed to load Stage 6C sessions'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreatePlan = useCallback(async () => {
    if (!selectedSession) return;
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/api/admin/psb-stage7/plans`,
        { s6c_plan_id: selectedSession.s6c_run_id }, { headers });
      const d = resp.data;
      setPlan(d.plan);
      setProductLines(d.product_lines || []);
      setExceptions(d.exception_register || []);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create Stage 7 plan');
    } finally {
      setLoading(false);
    }
  }, [selectedSession]);

  // Called when BulkExceptionResolver rebuilds the plan after auto-resolve
  const handleAutoResolveComplete = useCallback((result) => {
    if (result?.new_plan) {
      const d = result.new_plan;
      setPlan(d.plan);
      setProductLines(d.product_lines || []);
      setExceptions(d.exception_register || []);
    }
  }, []);

  const handleAccept = useCallback(async () => {
    if (!plan) return;
    setAccepting(true);
    try {
      const resp = await axios.post(`${API}/api/admin/psb-stage7/plans/${plan.run_id}/accept`,
        {}, { headers });
      setAcceptResult({ ok: true, ...resp.data });
      setPlan(prev => ({ ...prev, status: 'accepted' }));
      toast.success('Stage 7 calculation accepted.');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Accept failed';
      setAcceptResult({ ok: false, detail });
      toast.error(detail.slice(0, 80));
    } finally {
      setAccepting(false);
    }
  }, [plan]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="psb-stage7-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BarChart3 className="text-indigo-600" size={22} />
        <div>
          <h1 className="text-lg font-bold text-slate-900">PSB Stage 7 — Route B Calculation</h1>
          <p className="text-xs text-slate-500">
            Versioned snapshot · Product/headline reconciliation · Exception register · Acceptance gate
          </p>
        </div>
      </div>

      <StepHeader step={step} />

      {/* ── STEP 1: Session selector ──────────────────────────────────────── */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database size={16} className="text-indigo-500" />Stage 6C Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">
              Select an applied Stage 6C session. Stage 7 builds an immutable input snapshot
              from all applied PSB stages (S2–S6C) and runs the Route B stock equation.
            </p>
            {loading && (
              <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="s7-sessions-loading">
                <Loader2 size={14} className="animate-spin" />Loading sessions…
              </div>
            )}
            {!loading && sessions.length === 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700" data-testid="s7-no-sessions">
                <AlertTriangle size={13} className="inline mr-1" />
                No applied Stage 6C sessions found. Complete Stages 6A → 6B → 6C first.
              </div>
            )}
            <div className="space-y-2" data-testid="s7-session-list">
              {sessions.map(s => (
                <div key={s.s6c_run_id}
                  onClick={() => setSelectedSession(s)}
                  className={`rounded-xl border p-3 cursor-pointer transition-all hover:shadow-sm
                    ${selectedSession?.s6c_run_id === s.s6c_run_id
                      ? 'border-indigo-400 bg-indigo-50'
                      : 'border-slate-200 hover:border-indigo-200'}`}
                  data-testid={`s7-session-${s.session_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-800">
                        Session {s.session_id}
                        {s.session_reference && ` · ${s.session_reference}`}
                        {' '}
                        <span className="text-slate-400 font-normal">site {s.site_id}</span>
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        S6C: {s.s6c_run_id.slice(0, 16)}…
                        {s.existing_s7_run_id && (
                          <span className="ml-2 text-indigo-500">
                            Existing S7 plan: {s.existing_s7_run_id.slice(0, 8)}…
                          </span>
                        )}
                      </p>
                    </div>
                    {selectedSession?.s6c_run_id === s.s6c_run_id && (
                      <CheckCircle2 size={16} className="text-indigo-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleCreatePlan}
                disabled={!selectedSession || loading}
                data-testid="s7-create-plan-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {selectedSession?.existing_s7_run_id ? 'Open existing plan' : 'Run calculation'}
                <ChevronRight size={15} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 2: Coverage + Exception Register ─────────────────────────── */}
      {step === 2 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert size={16} className="text-red-500" />Source Coverage &amp; Exception Register
              <CoverageBadge pct={plan.coverage_pct} blocked={plan.has_material_exceptions} />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Coverage summary */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="s7-coverage-summary">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Total S1 products" value={plan.total_s1_products} testid="s7-total-products" />
                <Stat label="Covered (mapped)" value={plan.covered_products} highlight="green" testid="s7-covered-products" />
                <Stat label="Held (no S1 mapping)" value={plan.held_products} highlight={plan.held_products > 0 ? 'red' : undefined} testid="s7-held-products" />
                <Stat label="Coverage" value={`${Number(plan.coverage_pct).toFixed(2)}%`} highlight={plan.coverage_pct < 50 ? 'red' : plan.coverage_pct < 95 ? 'amber' : 'green'} testid="s7-coverage-pct" />
              </div>
            </div>

            {/* Block reason */}
            {plan.has_material_exceptions && (
              <div className="rounded-lg bg-red-50 border border-red-300 px-3 py-2 flex items-start gap-2" data-testid="s7-blocked-banner">
                <XCircle size={14} className="text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700 font-medium">{plan.blocked_reason}</p>
              </div>
            )}
            {!plan.has_material_exceptions && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-700 font-medium">
                  All products covered. No material exceptions. Calculation acceptance is unlocked.
                </p>
              </div>
            )}

            <ResidualBridge headline={plan.headline} plan={plan} />
            <ExceptionRegister exceptions={exceptions} summary={plan.exception_summary} />
            <FingerprintChain plan={plan} />

            {/* Source bridge — visible here regardless of material exception status */}
            <SourceBridgePanel
              planId={plan.run_id}
              headers={headers}
              onDeclared={() => {
                // Reload plan after a declaration to reflect updated gate state
                setLoading(true);
                axios.get(`${API}/api/admin/psb-stage7/plans/${plan.run_id}`, { headers })
                  .then(r => { if (r.data?.plan) setPlan(r.data.plan); })
                  .catch(() => {})
                  .finally(() => setLoading(false));
              }}
            />

            {plan.has_material_exceptions && (
              <BulkExceptionResolver
                planId={plan.run_id}
                headers={headers}
                onResolved={handleAutoResolveComplete}
              />
            )}

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => { setStep(1); setPlan(null); }}
                data-testid="s7-back-to-session-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              <Button onClick={() => setStep(3)} data-testid="s7-proceed-to-recon-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Reconciliation <ChevronRight size={15} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 3: Reconciliation ─────────────────────────────────────────── */}
      {step === 3 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList size={16} className="text-indigo-500" />Route B Reconciliation
              {plan.has_material_exceptions && (
                <span className="text-[10px] font-normal text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 ml-1">
                  PARTIAL — {plan.held_products} held
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <HeadlineCard headline={plan.headline} ytd={plan.ytd} blocked={plan.has_material_exceptions} />

            {productLines.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Per-Product Lines ({productLines.length} covered)
                </p>
                <ProductLinesTable lines={productLines} />
              </div>
            )}

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="s7-back-to-coverage-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              <Button onClick={() => setStep(4)} data-testid="s7-proceed-to-accept-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Acceptance gate <ChevronRight size={15} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 4: Acceptance gate ───────────────────────────────────────── */}
      {step === 4 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 size={16} className={plan.has_material_exceptions ? 'text-red-500' : 'text-emerald-500'} />
              Acceptance Gate
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="s7-acceptance-summary">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Plan ID" value={plan.run_id.slice(0, 16) + '…'} mono testid="s7-plan-id" />
                <Stat label="Session" value={plan.session_id} testid="s7-session-id" />
                <Stat label="Coverage" value={`${Number(plan.coverage_pct).toFixed(2)}%`}
                  highlight={plan.coverage_pct < 95 ? 'red' : 'green'} testid="s7-accept-coverage" />
                <Stat label="Status" value={plan.status}
                  highlight={plan.status === 'accepted' ? 'green' : plan.has_material_exceptions ? 'red' : 'green'}
                  testid="s7-plan-status" />
              </div>
            </div>

            {/* Block gate banner */}
            {plan.has_material_exceptions && (
              <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 space-y-2" data-testid="s7-accept-blocked">
                <div className="flex items-center gap-2">
                  <XCircle size={18} className="text-red-600 shrink-0" />
                  <p className="text-sm font-bold text-red-700">Calculation Acceptance BLOCKED</p>
                </div>
                <p className="text-xs text-red-600">{plan.blocked_reason}</p>
                <ul className="text-xs text-red-600 space-y-0.5 ml-4 list-disc">
                  <li>Calculation acceptance is locked</li>
                  <li>Full Report generation is locked</li>
                  <li>Locking is disabled until all mappings are resolved</li>
                </ul>
                <div className="mt-3 rounded-lg bg-slate-100 border border-slate-200 px-3 py-2">
                  <p className="text-xs text-slate-700">
                    <strong>To unblock:</strong> Return to Stage 1 and approve the {plan.held_products} unmapped
                    product mappings, then re-run Stages 4, 6A, 6B, 6C, and Stage 7.
                  </p>
                </div>
              </div>
            )}

            {!plan.has_material_exceptions && plan.status !== 'accepted' && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="s7-accept-ready">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 size={18} className="text-emerald-600" />
                  <p className="text-sm font-bold text-emerald-700">Ready to accept</p>
                </div>
                <p className="text-xs text-emerald-600">
                  All {plan.total_s1_products} products are covered. No material exceptions.
                  Accepting will lock the versioned snapshot and enable Full Report generation.
                </p>
              </div>
            )}

            {plan.status === 'accepted' && (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="s7-accepted-badge">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-emerald-600" />
                  <p className="text-sm font-bold text-emerald-700">
                    Stage 7 calculation accepted. Route B report generation unlocked.
                  </p>
                </div>
              </div>
            )}

            {/* Source bridge — shown in acceptance gate so reviewer can declare before accepting */}
            <SourceBridgePanel
              planId={plan.run_id}
              headers={headers}
              onDeclared={() => {}}
            />

            {acceptResult && !acceptResult.ok && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700"
                data-testid="s7-accept-error">
                <strong>BLOCKED:</strong> {acceptResult.detail}
              </div>
            )}

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(3)} data-testid="s7-back-to-recon-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              {!plan.has_material_exceptions && plan.status !== 'accepted' && (
                <Button onClick={handleAccept} disabled={accepting}
                  data-testid="s7-accept-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {accepting ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                  Accept Stage 7 calculation
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
